import React, { useCallback, useEffect, useState } from 'react';
import { AppBar, Avatar, Badge, Box, Button, CircularProgress, Divider, IconButton, Menu, MenuItem, Toolbar, Typography } from '@mui/material';
import { HiOutlineLogout } from 'react-icons/hi';
import { IoNotificationsOutline } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import rentalCrowLogo from '../assets/RentalCrow-Logo-Main.png';

const NOTIFICATION_SOURCES = [
  { key: 'vehicle', label: 'Vehicle Damage Claim', endpoint: '/get_submitted_open_forms', path: '/submitted-forms' },
  { key: 'shuttle', label: 'Shuttle Bus Claim', endpoint: '/get_submitted_shuttle_bus_open_forms', path: '/shuttle-bus/submitted-forms' },
  { key: 'windshield', label: 'Windshield/Tire Claim', endpoint: '/get_submitted_windshield_tire_open_forms', path: '/windshield-tire-claims/submitted-forms' },
];

const isSuperAdminRole = (role) => ['super admin', 'super administrator', 'superadmin'].includes(String(role || '').trim().toLowerCase());

export default function Header() {
  const loggedInUser = sessionStorage.getItem('user_token_ra');
  const foundUser = loggedInUser ? JSON.parse(loggedInUser) : null;
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const isSuperAdmin = isSuperAdminRole(foundUser?.selected_role);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!isSuperAdmin || !baseURL) return;
    setNotificationsLoading(true);
    const responses = await Promise.allSettled(
      NOTIFICATION_SOURCES.map((source) => axios.get(`${baseURL}${source.endpoint}`))
    );
    const unread = [];
    responses.forEach((response, index) => {
      if (response.status !== 'fulfilled') return;
      const source = NOTIFICATION_SOURCES[index];
      const rows = Array.isArray(response.value?.data) ? response.value.data : [];
      rows.filter((row) => !row?.opened_by_claims).forEach((row) => unread.push({
        ...row,
        notificationKey: `${source.key}-${row._id}`,
        notificationLabel: source.label,
        notificationPath: source.path,
      }));
    });
    unread.sort((a, b) => new Date(b.created_datetime || b.reportDateTime || 0) - new Date(a.created_datetime || a.reportDateTime || 0));
    setNotifications(unread);
    setNotificationsLoading(false);
  }, [baseURL, isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return undefined;
    fetchNotifications();
    const intervalId = window.setInterval(fetchNotifications, 60000);
    window.addEventListener('claim-notifications-refresh', fetchNotifications);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('claim-notifications-refresh', fetchNotifications);
    };
  }, [fetchNotifications, isSuperAdmin]);

  const handleLogout = () => {
    sessionStorage.setItem('user_token_ra', '');
    sessionStorage.clear();
    navigate('/');
    window.location.reload();
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        bgcolor: '#cfdaed',
        color: '#173971',
        borderBottom: '1px solid rgba(23, 57, 113, 0.08)',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
      }}
    >
      <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, md: 3.5 } }}>
        <Box component={Link} to="/" aria-label="Rental Crow dashboard" sx={{ display: 'flex', alignItems: 'center', color: '#173971', textDecoration: 'none' }}>
          <Box
            component="img"
            src={rentalCrowLogo}
            alt="Rental Crow"
            sx={{ display: 'block', width: 'auto', height: 52, borderRadius: 0.75 }}
          />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isSuperAdmin && (
            <>
              <IconButton aria-label={`${notifications.length} unread claim notifications`} onClick={(event) => setNotificationAnchor(event.currentTarget)} sx={{ color: '#173971', bgcolor: 'rgba(255,255,255,.5)', '&:hover': { bgcolor: 'rgba(255,255,255,.8)' } }}>
                <Badge badgeContent={notifications.length} color="error" max={99}>
                  <IoNotificationsOutline size={23} />
                </Badge>
              </IconButton>
              <Menu anchorEl={notificationAnchor} open={Boolean(notificationAnchor)} onClose={() => setNotificationAnchor(null)} PaperProps={{ sx: { width: 370, maxWidth: 'calc(100vw - 24px)', maxHeight: 480, borderRadius: 2.5 } }}>
                <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}><Box><Typography fontWeight={800}>New Claims</Typography><Typography variant="caption" color="text.secondary">Unread until the submitted record is opened</Typography></Box><Badge badgeContent={notifications.length} color="error" max={99} /></Box>
                <Divider />
                {notificationsLoading && !notifications.length && <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}><CircularProgress size={24} /></Box>}
                {!notificationsLoading && !notifications.length && <Box sx={{ px: 2, py: 4, textAlign: 'center' }}><Typography color="text.secondary">No new claims.</Typography></Box>}
                {notifications.map((item) => (
                  <MenuItem key={item.notificationKey} onClick={() => { setNotificationAnchor(null); navigate(item.notificationPath); }} sx={{ alignItems: 'flex-start', py: 1.4, whiteSpace: 'normal', borderBottom: '1px solid rgba(23,57,113,.06)' }}>
                    <Box><Typography fontSize={13.5} fontWeight={800}>{item.notificationLabel} #{item._id}</Typography><Typography fontSize={12.5} color="text.secondary">{item.branchLocationCode || item.claimOwningBranch || 'Unassigned'}{item.insurance_number ? ` • ${item.insurance_number}` : ''}</Typography><Typography fontSize={11.5} color="text.disabled">Submitted by {item.created_by || item.reportingEmployeeName || 'Unknown'}</Typography></Box>
                  </MenuItem>
                ))}
                {!!notifications.length && <Box sx={{ p: 1 }}><Button fullWidth size="small" onClick={fetchNotifications}>Refresh alerts</Button></Box>}
              </Menu>
            </>
          )}
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/profile')}
            title="Open profile"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              px: 1.5,
              py: 0.75,
              borderRadius: 10,
              bgcolor: 'rgba(255,255,255,0.45)',
              border: '1px solid rgba(23, 57, 113, 0.08)',
              color: 'inherit',
              font: 'inherit',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.75)' },
            }}
          >
            <Avatar
              sx={{
                width: 30,
                height: 30,
                bgcolor: 'primary.main',
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              {foundUser?.userName?.[0]?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Hi, {foundUser?.userName || 'User'}
            </Box>
          </Box>

          <Button
            onClick={handleLogout}
            variant="outlined"
            color="error"
            startIcon={<HiOutlineLogout />}
            sx={{
              borderRadius: 2,
              px: 1.75,
              py: 0.75,
              minWidth: 'auto',
            }}
          >
            Log Out
          </Button>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
